/* eslint-disable max-lines-per-function */
import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import './skillVerticalList.scss';
import {
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Typography,
} from '@mui/material';
import ButtonComponent from '../../../../Components/Button/button';
import { SKILL_MAP_DASHBOARD } from '../../../../Constants/strings';
import { AuthContext } from '../../../../Contexts/AuthContext';
import { mapSkills } from '../../apiCallUtil';
import { logError } from '../../../../Utils/logger';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from '../../../../Components/Snackbar/snackBarProvider';
import { ReactComponent as CustomCheckedIcon } from '../../../../Assets/images/svg/dar_custom_check_icon_1.svg';

const SkillVerticalList = ({
  list,
  employeeDetails,
  handleSelectedEmployeeError,
  setIsDirty,
}) => {
  const [skillsState, setSkillsState] = useState(
    list.map((item) => ({
      id: item.id,
      selectedSkillLevel: '',
      isCoreSkill: false,
      isChecked: false,
      skillName: item.skillName,
      wasCheckedOnSave: false, // New property to track save status
    })),
  );
  const [isSaveClicked, setIsSaveClicked] = useState(false);
  const [existingSkillIds, setExistingSkillIds] = useState([]);
  const [employee, setEmployee] = useState({});
  const { userProfile } = useContext(AuthContext);
  const { snackbarOpen } = useSnackbar();
  const navigate = useNavigate();

  useEffect(() => {
    setSkillsState(
      list.map((item) => ({
        id: item.id,
        skillId: item.skillId,
        selectedSkillLevel: '',
        isCoreSkill: false,
        isChecked: false,
        skillName: item.skillName,
        wasCheckedOnSave: false, // Initialize as false
      })),
    );
    setIsSaveClicked(false);
  }, [list]);

  useEffect(() => {
    setEmployee(employeeDetails);
  }, [employeeDetails]);

  const handleSkillLevelChange = (id, event) => {
    setSkillsState(
      skillsState.map((skill) =>
        skill.id === id
          ? { ...skill, selectedSkillLevel: event.target.value }
          : skill,
      ),
    );
  };

  const handleCoreSkillChange = (id, event) => {
    setSkillsState(
      skillsState.map((skill) =>
        skill.id === id
          ? { ...skill, isCoreSkill: event.target.checked }
          : skill,
      ),
    );
  };

  const handleCheckedChange = (id, event) => {
    setSkillsState(
      skillsState.map((skill) =>
        skill.id === id
          ? {
            ...skill,
            isChecked: event.target.checked,
            selectedSkillLevel: event.target.checked
              ? skill.selectedSkillLevel
              : '',
            isCoreSkill: event.target.checked ? skill.isCoreSkill : false,
            wasCheckedOnSave: event.target.checked
              ? skill.wasCheckedOnSave
              : false,
          }
          : skill,
      ),
    );
  };

  const formatSkillsData = (skillState) => {
    const formattedData = [];
    let hasError = false;

    skillState.forEach((skill) => {
      if (skill.isChecked) {
        if (skill.selectedSkillLevel === '') {
          hasError = true;
        } else {
          formattedData.push({
            skillId: skill.skillId,
            skillLevel: parseInt(skill.selectedSkillLevel, 10),
            isCoreSkill: skill.isCoreSkill,
          });
        }
      }
    });

    return { formattedData, hasError };
  };

  const handleSaveButtonClick = () => {
    setIsSaveClicked(true);
    setSkillsState(
      skillsState.map((skill) =>
        skill.isChecked ? { ...skill, wasCheckedOnSave: true } : skill,
      ),
    );

    if (employee?.fullName) {
      const { formattedData, hasError } = formatSkillsData(skillsState);
      if (!hasError) {
        submitData(formattedData);
      }
    } else {
      handleSelectedEmployeeError();
    }
  };

  const submitData = async (data) => {
    try {
      const mappedData = {
        userEmail: employee.emailId,
        userName: employee.fullName,
        vertical: employee.vertical,
        department: employee.department,
        userId: employee.employeeId,
        lastUpdatedBy: userProfile.user_name,
        skillDetails: data,
      };

      const result = await mapSkills(mappedData);
      if (result.failure) {
        if (result?.existingSkillIds) {
          setExistingSkillIds(result.existingSkillIds);
        }
        logError('Failed to update skill map');
        snackbarOpen(result.failure, 'warning');
      } else {
        setExistingSkillIds([]);
        snackbarOpen(SKILL_MAP_DASHBOARD.MAP_SKILL.SUCCESS_MESSAGE, 'success');
        navigate('/skills');
      }
    } catch (error) {
      logError('Error while mapping skills', error);
    }
  };

  const isSaveButtonEnabled = skillsState.some((skill) => {
    const isChecked = skill.isChecked;
    const hasSelectedSkillLevel = skill.selectedSkillLevel !== '';
    setIsDirty(isChecked && hasSelectedSkillLevel);
    return isChecked && hasSelectedSkillLevel;
  });

  const getWarningMessage = (skill) => {
    if (
      skill.wasCheckedOnSave
      && skill.isChecked
      && !skill.selectedSkillLevel
    ) {
      return 'Select skill level';
    }
    return '';
  };

  return (
    <>
      <div className="skill-vertical-list-conatiner">
        <div className="skill-vertical-grid-container">
          {skillsState.map((skill) => (
            <div
              key={skill.id}
              className={`grid-item ${skill.isChecked ? 'grid-item-checked' : ''}`}
            >
              <div className="skill-map-edit-content">
                <div className="section-1">
                  <div className="div-left">
                    <FormControlLabel
                      control={
                        <Checkbox
                          className="select-skill-checkbox"
                          checked={skill.isChecked}
                          onChange={(event) =>
                            handleCheckedChange(skill.id, event)
                          }
                        />
                      }
                      className="select-skill-label"
                      label={
                        <Typography className="select-skill-label-typography">
                          {skill.skillName}
                        </Typography>
                      }
                    />
                  </div>
                  <div className="div-right">
                    <FormControl>
                      <FormLabel
                        id={`skill-level-group-${skill.id}`}
                        className="skill-level-label"
                      >
                        Select Levels
                      </FormLabel>
                      <RadioGroup
                        row
                        aria-labelledby={`skill-level-group-${skill.id}`}
                        name={`row-radio-buttons-group-${skill.id}`}
                        value={skill.selectedSkillLevel}
                        onChange={(event) =>
                          handleSkillLevelChange(skill.id, event)
                        }
                        disabled={!skill.isChecked}
                      >
                        {[1, 2, 3, 4].map((level) => (
                          <FormControlLabel
                            key={level}
                            value={String(level)}
                            control={
                              <Radio
                                className="skill-level-radio"
                                disabled={!skill.isChecked}
                              />
                            }
                            label={String(level)}
                            labelPlacement="bottom"
                            className="skill-level-radio-label"
                          />
                        ))}
                      </RadioGroup>
                    </FormControl>
                    {existingSkillIds.includes(skill.skillId)
                      && skill.isChecked && (
                      <div className="skill-level-warning-message">
                        {skill.skillName}{' '}
                        {SKILL_MAP_DASHBOARD.MAP_SKILL.DUPLICATE_MAP_ERROR}
                      </div>
                    )}
                    {isSaveClicked && getWarningMessage(skill) && (
                      <div className="skill-level-warning-message">
                        {getWarningMessage(skill)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="section-2">
                  <FormControlLabel
                    control={
                      <Checkbox
                        className="core-skill-checkbox"
                        checked={skill.isCoreSkill}
                        onChange={(event) =>
                          handleCoreSkillChange(skill.id, event)
                        }
                        disabled={!skill.isChecked}
                        checkedIcon={<CustomCheckedIcon />}
                      />
                    }
                    className="core-skill-label"
                    label={
                      <Typography
                        className={`core-skill-label-typography
                        ${
            !skill.isChecked
              ? 'core-skill-label-typography-disabled'
              : ''
            }`}
                      >
                        Check if this is a core skill of the employee
                      </Typography>
                    }
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="map-skill-save-container">
        <ButtonComponent
          disabled={!isSaveButtonEnabled}
          label={SKILL_MAP_DASHBOARD.ADD.BUTTON_TEXT}
          variant="contained"
          onClick={handleSaveButtonClick}
          btnCss={
            !isSaveButtonEnabled ? 'map-skill-btn-disabled' : 'map-skill-btn'
          }
        ></ButtonComponent>
      </div>
    </>
  );
};

SkillVerticalList.propTypes = {
  list: PropTypes.array.isRequired,
  employeeDetails: PropTypes.object.isRequired,
  handleSelectedEmployeeError: PropTypes.func.isRequired,
  setIsDirty: PropTypes.func,
};

export default SkillVerticalList;
